'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import { getAppPortalContainer } from '@/lib/app-portal-container';
import { cn } from './cn';

/**
 * The new-system tooltip: canonical shadcn-style composition over Radix.
 *
 * Parity source is the app theme's MuiTooltip override (theme.ts): neutral-980
 * surface, common-white ink, caption size, semibold, arrow shown, placed below
 * its trigger with an 8px side offset, and a 100ms hover delay (MUI's
 * `enterDelay` default). Known, accepted deltas vs MUI (documented for review,
 * not silent): Radix ignores touch pointer moves; the app facade opts into explicit touch
 * timing where a caller needs it and there is no native `title`
 * fallback. Radix adds aria-describedby, Escape-to-close, and hover + focus
 * open, which is the behavioural floor MUI already provided.
 *
 * The portal target is the app shell (`#minerva-app-root`) via
 * getAppPortalContainer(), the same container MUI overlays default to, so
 * stacking against MUI dialogs/menus does not depend on DOM insertion order.
 * Content carries `z-[1500]` per AGENTS.md "UI System".
 */
export function TooltipProvider({
  delayDuration = 100,
  skipDelayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    />
  );
}

export const Tooltip = TooltipPrimitive.Root;

/** Public owned anatomy, unlike the retired library's private clone marker.
 * The inert-overlay guard and diagnostics can now detect the real trigger at
 * rest, before aria-describedby exists. Radix still owns its events/ref slot. */
export const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>((props, ref) => (
  <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} ref={ref} />
));
TooltipTrigger.displayName = 'TooltipTrigger';

/**
 * 4px radius is MUI's `shape.borderRadius` (the tooltip's default), not a
 * shadcn default — parity is the requirement for the swapped surfaces.
 */
export interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  /** Explicit DOM ownership; defaults to the same app-root portal as other overlays. */
  portal?: boolean;
  portalContainer?: HTMLElement | null;
}
export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    {
      className,
      side = 'bottom',
      sideOffset = 8,
      portal = true,
      portalContainer,
      onEscapeKeyDown,
      ...props
    },
    ref
  ) => {
    const content = (
      <TooltipPrimitive.Content
        ref={ref}
        className={cn(
          'z-[1500] max-w-[300px] rounded-[4px] bg-[var(--minerva-palette-neutral-980)] px-[calc(var(--minerva-spacing)*1.5)] py-[calc(var(--minerva-spacing)*1)] font-semibold text-[var(--minerva-palette-common-white)] text-xs shadow',
          'data-[state=delayed-open]:animate-tooltip-in data-[state=instant-open]:animate-tooltip-in',
          className
        )}
        side={side}
        sideOffset={sideOffset}
        onEscapeKeyDown={(event) => {
          onEscapeKeyDown?.(event);
          // Passive help must not swallow Escape at document capture before an
          // enclosing nonmodal surface receives it. Radix modal owners already
          // defer to their highest dismissable layer; menus/popovers retain
          // their own interactive Escape boundary.
        }}
        {...props}
      />
    );
    return portal ? (
      <TooltipPrimitive.Portal
        container={portalContainer ?? getAppPortalContainer() ?? undefined}
      >
        {content}
      </TooltipPrimitive.Portal>
    ) : (
      content
    );
  }
);
TooltipContent.displayName = 'TooltipContent';

export const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow>
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Arrow
    data-slot="tooltip-arrow"
    ref={ref}
    className={cn('fill-[var(--minerva-palette-neutral-980)]', className)}
    {...props}
  />
));
TooltipArrow.displayName = 'TooltipArrow';
